import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'

import { IconPlus, IconPlusInBox, IconTwoPeople } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { RadioButton } from 'components-ref/atoms'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import { AddFansGroupModal } from 'pages/fans/components/AddFansGroupModal'
import { getGroupsFromFan } from 'services/endpoints/fanGroup'

import SortModal from '../Sort/SortModal'

import './_add-user-to-groups.scss'

type GroupRow = {
  id: string
  name: string
  slug: string
  default: boolean | number
  belongs: boolean
  count: number
}

interface AddUserToGroupsProps {
  applyFn: (_item: string[]) => void
  id: string
}
const asBool = (v: any) => v === true || v === 1 || v === '1'

const normalizeGroupsResponse = (resp: any): GroupRow[] => {
  // expected: { status, code, data: [...] } OR already-unwrapped array
  const rows = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : []
  return rows.map((g: any) => ({
    id: String(g.id),
    name: String(g.name ?? ''),
    slug: String(g.slug ?? ''),
    default: g.default,
    belongs: asBool(g.belongs),
    count: Number(g.count ?? 0),
  }))
}

const AddUserToGroups = ({ applyFn, id }: AddUserToGroupsProps) => {
  const { t } = useTranslation()

  const [searchTerm, setSearchTerm] = useState('')
  const [sortModalOpen, setSortModalOpen] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectAll, setSelectAll] = useState(false)
  const [sortOptions, setSortOptions] = useState<{
    asc_or_desc: '' | 'asc' | 'desc'
    type: '' | 'name' | 'number_of_fans'
  }>({
    asc_or_desc: '',
    type: ''
  })
  const modalData = useModalContext()
  const [radioArray, setRadioArray] = useState<string[]>([])
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const queryClient = useQueryClient()

const handleGroupCreated = (createdGroup: any) => {
  const queryKey = [
    'fan-groups-membership',
    id
  ]

  const newGroup: GroupRow = {
    id: String(createdGroup.id),
    name: String(createdGroup.name ?? ''),
    slug: String(createdGroup.slug ?? ''),
    default: createdGroup.default ?? 0,
    belongs: false,
    count: Number(createdGroup.count ?? 0)
  }

  queryClient.setQueryData<GroupRow[]>(
    queryKey,
    old => [
      ...(old ?? []),
      newGroup
    ]
  )

  setAddModalOpen(false)
}

  // single query: returns ALL groups + belongs + count
  const { data: rawGroups, isLoading } = useQuery(
    ['fan-groups-membership', id],
    async () => await getGroupsFromFan(id),
  )
  const groups = useMemo(() => normalizeGroupsResponse(rawGroups), [rawGroups])
  useEffect(() => {
    if (!groups.length) return

    setRadioArray(
      groups
        .filter(group => group.belongs)
        .map(group => group.id)
    )
  }, [groups])
  const sortListsGroups = [
    { value: 'number_of_fans', name: t('numberOfFans') },
    { value: 'name', name: t('name') },
  ]

  const sortElementsOrder = [
    { value: 'asc', name: t('ascending') },
    { value: 'desc', name: t('descending') }
  ]

  // only show non-default groups in picker (matching your old behavior)
  const visibleGroups = useMemo(() => {
    const q = searchTerm.toLowerCase().trim()
    let list = groups.filter(g => !asBool(g.default))

    if (q) {
      list = list.filter(g => g.name.toLowerCase().trim().includes(q))
    }

    if (sortOptions.type && sortOptions.asc_or_desc) {
      if (sortOptions.type === 'name') {
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
        list = list.slice().sort((a, b) => {
          const res = collator.compare(a.name, b.name)
          return sortOptions.asc_or_desc === 'asc' ? res : -res
        })
      }

      if (sortOptions.type === 'number_of_fans') {
        list = list.slice().sort((a, b) => {
          const res = (a.count ?? 0) - (b.count ?? 0)
          return sortOptions.asc_or_desc === 'asc' ? res : -res
        })
      }
    }

    return list
  }, [groups, searchTerm, sortOptions])

  const toggleCheckbox = (groupId: string) => {
    setRadioArray(prev => {
      if (prev.includes(groupId)) return prev.filter(x => x !== groupId)
      return [...prev, groupId]
    })
  }

  // helper: remove only visible group ids from selection
  const removeVisibleFromSelection = (prev: string[]) => {
    if (!visibleGroups.length) return prev
    const visibleIds = new Set(visibleGroups.map(g => g.id))
    return prev.filter(id => !visibleIds.has(id))
  }

  const selectAllFn = () => {
    // 0 results => never select all
    if (!visibleGroups.length) return

    if (selectAll) {
      // unselect only visible items (do not wipe selections outside current filter/search)
      setRadioArray(prev => removeVisibleFromSelection(prev))
      setSelectAll(false)
      return
    }

    // select all visible items (merge, don't duplicate)
    setRadioArray(prev => {
      const set = new Set(prev)
      for (const g of visibleGroups) set.add(g.id)
      return Array.from(set)
    })
    setSelectAll(true)
  }

  useEffect(() => {
    // 0 results => selectAll must be false
    if (!visibleGroups.length) {
      setSelectAll(false)
      return
    }
    setSelectAll(visibleGroups.every(g => radioArray.includes(g.id)))
  }, [visibleGroups, radioArray])

  return (
    <div className='add-users-to-groups-modal-container'>
      <LayoutHeader
        type='search-with-buttons'
        searchValue={searchTerm}
        searchFn={(val: string) => setSearchTerm(val)}
        clearFn={() => setSearchTerm('')}
        additionalProps={{ placeholder: t('searchGroups') }}
        searchSettings={{ hideIconOnMobile: true }}
        buttons={[
          {
            type: 'sort',
            customClickFn: () => setSortModalOpen(true)
          },
          {
            type: 'custom',
            icon: <IconPlus />,
            color: 'white',
            action: () => setAddModalOpen(true),
            customClass: 'add-user-to-groups-add-button'
          }
        ]}
      />

      <div className='add-user-to-groups-separator'></div>

      <div className='add-user-to-groups-select-all-container'>
        <RadioButton active={selectAll} clickFn={selectAllFn} />
        <p className='add-user-to-groups-select-all-text'>{t('selectAll')}</p>
      </div>

      <div className='add-user-to-groups-container'>
        {isLoading && (
          <div className='add-user-to-groups-loader'>
            <div className='loader'></div>
          </div>
        )}

        {!isLoading &&
          visibleGroups.map(group => (
            <ActionCard
              key={group.id}
              icon={<IconTwoPeople />}
              text={group.name}
              subtext={`${group.count ?? 0} fans`}
              absFix
              hasRadio
              toggleActive={radioArray.includes(group.id)}
              toggleFn={() => toggleCheckbox(group.id)}
            />
          ))}

        {!isLoading && (
          <ActionCard
            icon={<IconPlusInBox width='22' height='22' />}
            text={t('addNewGroup')}
            addNewText
            clickFn={() => setAddModalOpen(true)}
          />
        )}
      </div>

<div className='add-user-to-groups-button-container-wrapper'>
  <div className='add-user-to-groups-button-container'>
    <Button
      text={t('cancel')}
      color='grey'
      width='13'
      height='3'
      font='mont-14-normal'
      customClass='add-user-to-groups-cancel-button'
      clickFn={modalData.clearModal}
    />

    <Button
      text={t('save')}
      color='black'
      width='13'
      height='3'
      font='mont-14-normal'
      customClass={`add-user-to-groups-button ${
        isSubmitLoading ? 'add-user-to-groups-button-loading' : ''
      }`}
      clickFn={() => {
        const customGroupIds = groups
          .filter(
            group =>
              !asBool(group.default) &&
              radioArray.includes(group.id)
          )
          .map(group => group.id)

        applyFn(customGroupIds)
        setIsSubmitLoading(true)
      }}
    />
  </div>
</div>

      <ModalWrapper
        open={sortModalOpen}
        setOpen={setSortModalOpen}
        customClass='add-user-to-groups-modal-wrapper-card'
      >
        <div className='add-user-to-groups-modal-title'>{t('sortBy')}</div>
        <SortModal
          elements={{
            first_section: sortListsGroups,
            second_section: sortElementsOrder
          }}
          sortingProps={{
            selectedSort: sortOptions.type,
            selectedOrder: sortOptions.asc_or_desc
          }}
          hasResetBtn={false}
          dontClearModal={true}
          applyFn={(val1: string, val2: string) => {
            setSortModalOpen(false)
            if (val1.trim() === '' || val2.trim() === '') {
              setSortOptions({ asc_or_desc: '', type: '' })
              return
            }

            const type = val1 as 'name' | 'number_of_fans'
            const order = val2 as 'asc' | 'desc'
            setSortOptions({ asc_or_desc: order, type })
          }}
        />
      </ModalWrapper>

      <ModalWrapper
        open={addModalOpen}
        setOpen={setAddModalOpen}
        customClass='add-user-to-groups-modal-wrapper-card add-user-to-groups-modal-wrapper-top'
      >
        <div className='add-user-to-groups-modal-title'>{t('addNewGroup')}</div>
        <AddFansGroupModal
          dontClearModal={true}
          customCloseFn={() =>
            setAddModalOpen(false)
          }
          onCreated={handleGroupCreated}
        />
      </ModalWrapper>
    </div>
  )
}

export default AddUserToGroups